import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ConcurrentCareEpisode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { ProfileCardBadge } from '../../molecules/profileCardBadge/ProfileCardBadge'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ConcurrentCareProps = {
  concurrentCare: ConcurrentCareEpisode[]
}

const BaseContainer = styled.View<{
  gapSpacing?: keyof ThemeType['spacing']
  flexDirection?: 'row' | 'column'
  alignSelf?: 'stretch' | undefined
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
  flexWrap?: 'wrap' | 'nowrap'
}>(
  ({
    theme: { spacing },
    gapSpacing = '16px',
    flexDirection = 'column',
    alignSelf = 'stretch',
    justifyContent,
    flexWrap,
  }) => ({
    flexDirection,
    gap: spacing[gapSpacing],
    alignItems: 'flex-start',
    ...(alignSelf && { alignSelf }),
    ...(justifyContent && { justifyContent }),
    ...(flexWrap && { flexWrap }),
  }),
)

// Consolidated card component for both single and multi layouts
const ConcurrentCareCard = styled.View<{
  isVerticalLayout?: boolean
}>(({ theme: { spacing, colors }, isVerticalLayout = true }) => ({
  backgroundColor: colors.backgroundElement,
  borderRadius: spacing['16px'],
  padding: spacing['24px'],
  flexDirection: isVerticalLayout ? 'column' : 'row',
  gap: isVerticalLayout ? spacing['16px'] : undefined,
  alignItems: 'flex-start',
  justifyContent: isVerticalLayout ? 'flex-start' : 'space-between',
  alignSelf: isVerticalLayout ? 'stretch' : undefined,
  flexBasis: isVerticalLayout ? undefined : 'calc(50% - 12px)',
  flexGrow: isVerticalLayout ? undefined : 0,
  flexShrink: isVerticalLayout ? undefined : 1,
  minWidth: isVerticalLayout ? undefined : 0,
}))

// Layout for 1 episode - horizontal details

const SingleCardDetailsContainer = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  gap: spacing['32px'],
  alignItems: 'flex-start',
  alignSelf: 'stretch',
}))

const SingleCardDetailColumn = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
  alignItems: 'flex-start',
  flex: 1,
  minWidth: 0,
}))

// Layout for 2 episodes - vertical details
const MultiCardDetailsContainer = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  gap: spacing['64px'],
  alignItems: 'flex-start',
}))

// Consolidated styled component for both label and value with optional width
const MultiCardDetailItem = styled.View<{ width?: number }>(({ width }) => ({
  justifyContent: 'center',
  ...(width && { width }),
}))

// Helper component for detail rows to reduce repetition
const DetailRow: FunctionComponent<{
  label: string
  value: string
}> = ({ label, value }) => {
  const { colors } = useTheme() as ThemeType
  return (
    <BaseContainer gapSpacing='8px' flexDirection='row' alignSelf={undefined}>
      <MultiCardDetailItem width={90}>
        <BodyText text={label} size={BodyTextSize.CAPTION} color={colors.textSecondary} />
      </MultiCardDetailItem>
      <MultiCardDetailItem>
        <BodyText text={value} size={BodyTextSize.DEFAULT} color={colors.textPrimary} />
      </MultiCardDetailItem>
    </BaseContainer>
  )
}

// Helper component for single card detail columns to reduce repetition
const DetailColumn: FunctionComponent<{
  label: string
  value: string
}> = ({ label, value }) => {
  const { colors } = useTheme() as ThemeType
  return (
    <SingleCardDetailColumn>
      <BodyText text={label} size={BodyTextSize.CAPTION} color={colors.textSecondary} />
      <Title text={value} size={TitleSize.SMALL} color={colors.textPrimary} />
    </SingleCardDetailColumn>
  )
}

const EpisodeCard: FunctionComponent<{
  episode: ConcurrentCareEpisode
  labels: {
    provider: string
    startDate: string
    nextSession: string
  }
  dashText: string
  isVerticalLayout?: boolean
  id?: string
}> = ({ episode, labels, dashText, isVerticalLayout = false, id = 'ConcurrentCareEpisodeCard' }) => {
  if (isVerticalLayout) {
    return (
      <ConcurrentCareCard isVerticalLayout={true} testID={tID(id)}>
        <BaseContainer gapSpacing='8px' alignSelf='stretch'>
          <BaseContainer gapSpacing='8px' justifyContent='center' alignSelf={undefined}>
            <ProfileCardBadge text={episode.programName} />
          </BaseContainer>

          <SingleCardDetailsContainer>
            <DetailColumn label={labels.provider} value={episode.providerName} />
            <DetailColumn label={labels.startDate} value={episode.episodeStartDate} />
            <DetailColumn label={labels.nextSession} value={episode.nextSessionDate || dashText} />
          </SingleCardDetailsContainer>
        </BaseContainer>
      </ConcurrentCareCard>
    )
  }

  // Horizontal layout for smaller screens
  return (
    <ConcurrentCareCard isVerticalLayout={false} testID={tID(id)}>
      <MultiCardDetailsContainer>
        <BaseContainer gapSpacing='8px' alignSelf={undefined}>
          <DetailRow label={labels.provider} value={episode.providerName} />
          <DetailRow label={labels.startDate} value={episode.episodeStartDate} />
          <DetailRow label={labels.nextSession} value={episode.nextSessionDate || dashText} />
        </BaseContainer>
      </MultiCardDetailsContainer>

      <ProfileCardBadge text={episode.programName} />
    </ConcurrentCareCard>
  )
}

export const ConcurrentCare: FunctionComponent<ConcurrentCareProps> = ({ concurrentCare }) => {
  const { breakpoints } = useTheme() as ThemeType
  const intl = useIntl()

  // Memoized text constants to avoid repeated intl.formatMessage calls
  const dashText = intl.formatMessage({
    defaultMessage: '-',
    description: 'Dash placeholder for missing next session date',
  })

  const labels = {
    provider: intl.formatMessage({
      defaultMessage: 'Provider',
      description: 'Label for provider name in concurrent care',
    }),
    startDate: intl.formatMessage({
      defaultMessage: 'Start date',
      description: 'Label for start date in concurrent care',
    }),
    nextSession: intl.formatMessage({
      defaultMessage: 'Next session',
      description: 'Label for next session date in concurrent care',
    }),
    title: intl.formatMessage({
      defaultMessage: 'Concurrent care',
      description: 'Title for concurrent care section',
    }),
  }

  const shouldUseSingleCardLayout = concurrentCare.length === 1
  const shouldUseVerticalStackLayout = !breakpoints.isMinWidthLaptop

  if (concurrentCare.length === 0) {
    return null
  }

  return (
    <>
      <Divider style={{ marginVertical: 40 }} />
      <BaseContainer testID={tID('ConcurrentCare')}>
        <Title text={labels.title} size={TitleSize.MEDIUM} />
        {shouldUseSingleCardLayout ? (
          // Single episode - single card layout
          <EpisodeCard
            episode={concurrentCare[0]}
            labels={labels}
            dashText={dashText}
            isVerticalLayout={true}
            id={'single-ConcurrentCareEpisodeCard'}
          />
        ) : shouldUseVerticalStackLayout ? (
          // vertical stack of multiple cardslayout for laptop+ screens
          <BaseContainer>
            {concurrentCare.map((concurrentCareEpisode, index) => (
              <EpisodeCard
                key={`concurrent-care-episode-${index}`}
                episode={concurrentCareEpisode}
                labels={labels}
                dashText={dashText}
                isVerticalLayout={true}
                id={`vertical-ConcurrentCareEpisodeCard-${index}`}
              />
            ))}
          </BaseContainer>
        ) : (
          // multiple cards side by side
          <BaseContainer flexDirection='row' flexWrap='wrap' justifyContent='space-between'>
            {concurrentCare.map((concurrentCareItem, index) => (
              <EpisodeCard
                key={`concurrent-care-episode-${index}`}
                episode={concurrentCareItem}
                labels={labels}
                dashText={dashText}
                isVerticalLayout={false}
                id={`horizontal-ConcurrentCareEpisodeCard-${index}`}
              />
            ))}
          </BaseContainer>
        )}
      </BaseContainer>
    </>
  )
}
